import { Seo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, Sparkles, Loader2, Tag, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { EmbeddedCheckoutPanel } from "../components/EmbeddedCheckoutPanel";
import { useAuth } from "../context/AuthContext";
import { useBooking } from "../context/BookingContext";
import api, { formatApiError } from "../lib/api";
import { IMG } from "../lib/media";

export default function Membership() {
  const { openBooking } = useBooking();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [offerStarted, setOfferStarted] = useState(false);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");

  useEffect(() => {
    api.get("/membership-tiers").then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setConfirming(true);
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Membership activated — welcome to the club!");
          await refreshUser();
          navigate("/dashboard");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) setTimeout(poll, 1500);
      else { setConfirming(false); toast.error("Still confirming your payment. Check your dashboard shortly."); }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    if (!user) {
      toast("Sign in to use a promo code", { description: "Create a free member account first." });
      navigate("/register");
      return;
    }
    setPromoBusy(true);
    try {
      const { data } = await api.post("/member/validate-promo", { code });
      setAppliedPromo(data);
      const off = data.discount_type === "percent" ? `${data.value}% off` : `$${data.value} off`;
      toast.success(`Promo ${data.code} applied — ${off}`);
    } catch (e) {
      setAppliedPromo(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid promo code");
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setAppliedPromo(null); setPromoInput(""); };

  const promoLabel = appliedPromo
    ? appliedPromo.discount_type === "percent"
      ? `${appliedPromo.value}% off`
      : `$${appliedPromo.value} off`
    : null;

  const joinFree = async (tier) => {
    setBusy(tier.slug);
    try {
      await api.post("/member/join-tier", { name: user.name, email: user.email, tier: tier.name });
      await refreshUser();
      toast.success(`Welcome to ${tier.name}!`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  const subscribe = async (tier) => {
    setBusy(tier.slug);
    try {
      const { data } = await api.post("/member/subscribe", {
        plan_key: tier.plan_key,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || null,
      });
      setClientSecret(data.client_secret);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const handleJoin = (tier) => {
    if (!user) {
      toast("Create a free member account to join", { description: "Sign up takes less than a minute." });
      navigate(`/register`);
      return;
    }
    if (tier.free) joinFree(tier);
    else subscribe(tier);
  };

  useEffect(() => {
    if (params.get("offer") !== "insider99" || offerStarted || sessionId) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/membership?offer=insider99")}`);
      return;
    }
    setOfferStarted(true);
    subscribe({ slug: "insider-99", plan_key: "insider-99" });
    // eslint-disable-next-line
  }, [user, offerStarted]);

  if (sessionId && confirming) {
    return (
      <div data-testid="membership-confirming">
        <Seo title="Membership" path="/membership" />
        <PageHeader overline="VIP Membership" title="Confirming your membership" subtitle="One moment while we activate your perks." />
        <div className="max-w-lg mx-auto px-6 pb-24 flex flex-col items-center gap-3 text-[#8A9492]">
          <Loader2 className="h-6 w-6 animate-spin" /> Confirming your payment…
        </div>
      </div>
    );
  }

  if (clientSecret) {
    return (
      <div data-testid="membership-checkout-page">
        <Seo title="Membership Checkout" path="/membership" />
        <PageHeader overline="VIP Membership" title="Complete your membership" subtitle="Secure payment — you never leave our site." />
        <div className="max-w-2xl mx-auto px-6 pb-24">
          <EmbeddedCheckoutPanel clientSecret={clientSecret} onBack={() => { setClientSecret(null); setBusy(null); }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="membership-page">
      <Seo title="VIP Membership" description="Join the Iridescent VIP membership in Clinton Township, MI for exclusive pricing, priority booking, rewards and members-only perks on aesthetics and skincare." path="/membership" />
      <PageHeader
        overline="VIP Membership"
        title="Membership that pays for itself"
        subtitle="Join free to earn points every visit, or unlock VIP facials, exclusive pricing and member-only perks with an Insider membership."
      />

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <button
              onClick={() => openBooking("First Time Client Lift & Glow + Consult")}
              data-testid="membership-first-visit-cta"
              className="group w-full text-left block rounded-[1.5rem] overflow-hidden relative bg-ink text-white"
            >
              <img src={IMG.facial} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              <div className="relative px-6 py-7 md:px-10 md:py-9 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                <div className="flex-1">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] iri-text font-medium">
                    <Sparkles className="h-3.5 w-3.5" /> New client offer
                  </span>
                  <h3 className="font-heading text-xl md:text-2xl font-light mt-2">
                    First Time Visit — Lift &amp; Glow Facial <span className="text-white/70">+ Complimentary Consultation</span>
                  </h3>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="font-heading text-3xl font-light">$99<span className="text-lg">.95</span></p>
                  </div>
                  <span className="inline-flex items-center gap-2 iri-gradient text-ink font-medium rounded-full h-11 px-6 group-hover:scale-105 transition-transform">
                    Reserve now <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </button>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Stagger className="grid md:grid-cols-3 gap-6 items-stretch">
            {tiers.map((t) => {
              const isCurrent = user?.tier === t.name;
              const discountedPrice = () => {
                const p = Number(t.price);
                if (!appliedPromo) return p;
                const d = appliedPromo.discount_type === "percent" ? (p * appliedPromo.value) / 100 : appliedPromo.value;
                return Math.max(p - d, 0.5);
              };
              const showDiscount = !t.free && appliedPromo;
              const label = isCurrent
                ? "Your current membership"
                : t.free
                ? "Join free"
                : showDiscount
                ? `Subscribe · $${discountedPrice()}/mo first month`
                : `Subscribe · $${t.price}/mo`;
              return (
                <StaggerItem key={t.slug} className="h-full">
                  <div
                    data-testid={`tier-card-${t.slug}`}
                    className={`relative h-full rounded-[1.75rem] p-8 flex flex-col ${
                      t.featured
                        ? "bg-ink text-white md:-translate-y-4 shadow-2xl shadow-blue-900/20"
                        : "bg-white border border-slate-100"
                    }`}
                  >
                    {t.featured && (
                      <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-xs iri-gradient text-ink font-medium px-3 py-1 rounded-full">
                        <Sparkles className="h-3 w-3" /> Most popular
                      </span>
                    )}
                    <h3 className={`font-heading text-2xl font-medium ${t.featured ? "text-white" : "text-ink"}`}>
                      {t.name}
                    </h3>
                    <p className={`text-sm mt-1 ${t.featured ? "text-white/60" : "text-[#8A9492]"}`}>
                      {t.tagline}
                    </p>

                    <div className="mt-6 flex items-end gap-2">
                      {t.free ? (
                        <span className={`font-heading text-5xl font-light ${t.featured ? "text-white" : "text-ink"}`}>Free</span>
                      ) : (
                        <>
                          <span className={`font-heading text-5xl font-light ${t.featured ? "text-white" : "text-ink"}`}>
                            ${t.price}
                          </span>
                          <span className={`mb-1.5 text-sm ${t.featured ? "text-white/60" : "text-[#8A9492]"}`}>/month</span>
                        </>
                      )}
                    </div>
                    {t.regular_price && (
                      <p className={`mt-1 text-sm ${t.featured ? "text-white/50" : "text-[#8A9492]"}`}>
                        <span className="line-through">${t.regular_price}/mo</span>{" "}
                        <span className="text-iri-mid font-medium">special pricing</span>
                      </p>
                    )}

                    <ul className="mt-7 space-y-3 flex-1">
                      {t.benefits.map((b) => (
                        <li key={b} className={`flex items-start gap-3 text-sm ${t.featured ? "text-white/80" : "text-[#5C6664]"}`}>
                          <Check className={`h-4 w-4 mt-0.5 shrink-0 ${t.featured ? "text-iri-start" : "text-iri-mid"}`} />
                          {b}
                        </li>
                      ))}
                    </ul>

                    {t.facials && (
                      <div className={`mt-5 rounded-xl p-4 text-xs ${t.featured ? "bg-white/10" : "bg-stone"}`}>
                        <p className={`font-medium mb-1.5 ${t.featured ? "text-white" : "text-ink"}`}>Choose your VIP facials:</p>
                        <p className={t.featured ? "text-white/70" : "text-[#5C6664]"}>{t.facials.join(" · ")}</p>
                      </div>
                    )}

                    <Button
                      data-testid={`join-tier-${t.slug}`}
                      disabled={isCurrent || busy === t.slug}
                      onClick={() => handleJoin(t)}
                      className={`mt-8 rounded-full h-12 w-full font-medium ${
                        t.featured
                          ? "iri-gradient text-ink hover:scale-105 transition-transform"
                          : "bg-ink text-white hover:bg-ink/90"
                      } ${isCurrent ? "opacity-60" : ""}`}
                    >
                      {busy === t.slug ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
                    </Button>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>

          <Reveal className="mt-8">
            <div
              className="max-w-md mx-auto bg-white border border-slate-100 rounded-2xl p-5"
              data-testid="promo-box"
            >
              <p className="text-sm font-medium text-ink flex items-center gap-2">
                <Tag className="h-4 w-4 text-iri-mid" /> Have a promo code?
              </p>
              {appliedPromo ? (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-stone px-4 py-3" data-testid="promo-applied">
                  <span className="text-sm text-ink">
                    <span className="font-mono font-medium">{appliedPromo.code}</span> — {promoLabel}
                  </span>
                  <button onClick={clearPromo} data-testid="promo-clear" className="text-[#8A9492] hover:text-red-500 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <Input
                    placeholder="Enter code"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                    data-testid="promo-input"
                    className="rounded-full h-11 uppercase"
                  />
                  <Button
                    onClick={applyPromo}
                    disabled={promoBusy || !promoInput.trim()}
                    data-testid="promo-apply"
                    className="rounded-full h-11 px-6 bg-ink text-white hover:bg-ink/90 shrink-0"
                  >
                    {promoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}
              <p className="mt-2 text-xs text-[#8A9492]">Discount applies to your Insider or Insider Preferred checkout.</p>
            </div>
          </Reveal>

          <Reveal className="mt-10">
            <p className="text-center text-xs text-[#8A9492] max-w-2xl mx-auto">
              Paid memberships are billed monthly. Offers subject to change; first-visit offers valid at your first visit.
              All plans are based on a 12-month commitment. Payment plans available through our financing partners.
            </p>
          </Reveal>

          <Reveal className="mt-14">
            <div className="relative rounded-[2rem] overflow-hidden">
              <img src={IMG.facial} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="relative bg-ink/55 backdrop-blur-[1px] px-8 py-16 md:p-20 text-center">
                <h2 className="font-heading text-2xl md:text-4xl font-light text-white max-w-2xl mx-auto">
                  Not sure which membership is right for you?
                </h2>
                <p className="text-white/70 mt-4 max-w-lg mx-auto">
                  Book a complimentary consultation and we'll design a plan around your goals.
                </p>
                <Button
                  onClick={() => navigate("/contact")}
                  data-testid="membership-consult-button"
                  className="mt-7 bg-white text-ink rounded-full h-12 px-8 hover:bg-white/90"
                >
                  Request a consultation
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
